import * as DateFNS from 'date-fns';
import {
  TravelPlacePricingAndBookingDetailsPayload,
  PreferredHotelProviderType,
  PriceComparisonPayload,
  PriceComparisonType,
  TravelPlacePricingAndBookingDetailsFragment,
  TravelPlacePricingAndBookingDetailsFragmentDoc,
  TravelCurrencyCode,
  useTravelPlacesPricingAndBookingDetailsQuery
} from '@graphql/generated';
import { useRemoteFeatureFlag } from '@shared/core/featureFlags/remoteFeatureFlags';
import { ApolloClient, ApolloError, makeVar, useApolloClient, useReactiveVar } from '@apollo/client';
import { useEffect, useMemo, useRef } from 'react';
import { useLocaleContext } from '@shared/core/i18n/LocaleContext';
import { addError } from '@shared/utils/logger';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useCheckInCheckoutDates } from '../useCheckInCheckoutDates';
import { useTravelWhiteLabelCurrency } from '../useTravelWhiteLabelCurrency';

const queryStatusVar = makeVar<{
  loading: boolean;
  called: boolean;
  error?: ApolloError;
}>({ loading: false, called: false, error: undefined });

const translatedPrice = (priceInMinorUnits: number, digits: number, currency: string, locale: NavigatorLanguage['language'] = 'en-US') => {
  return Math.round(priceInMinorUnits / Math.pow(10, digits)).toLocaleString(locale, {
    style: 'currency',
    maximumSignificantDigits: 3,
    currency
  });
};

const isValidPriceComparisonType = (value: string): value is PriceComparisonType => Object.values(PriceComparisonType).includes(value as PriceComparisonType);

const isValidPreferredHotelProviderType = (value: string): value is PreferredHotelProviderType =>
  Object.values(PreferredHotelProviderType).includes(value as PreferredHotelProviderType);

const isValidMarginPercentage = (value: number): value is number => typeof value === 'number' && !isNaN(value) && value >= 0 && value <= 100;

const isValidPriceComparePayload = (payload: PriceComparisonPayload | undefined) => {
  if (!payload) return false;
  return isValidPriceComparisonType(payload.factor) && isValidPreferredHotelProviderType(payload.marginForProvider) && isValidMarginPercentage(payload.marginPercentage);
};

/**
 *
 * @param travelPlaceIds - array of travelPlaceIds to fetch pricing
 * @param travelPlaceIdType = accommodationPlace | joyPlace - To identify which type of travelPlaceIds either accommodationPlace or joyPlace
 * @param targetDate - optional date (in milliseconds) for check-in and check-out (-1 day and +1 day from event date respectively)
 */
export const useTravelPlacesPricingAndBookingDetails = (travelPlaceIds: string[], travelPlaceIdType: 'accommodationPlace' | 'joyPlace', targetDate?: Maybe<number>) => {
  const { eventInfo, loading: loadingEventInfo } = useEventInfo();
  const eventDate = eventInfo?.finalizedDate?.dateString || undefined;
  const { checkIn: checkInDate, checkOut: checkOutDate } = useCheckInCheckoutDates({ eventDate });

  const [currencyCode, { loading: loadingCurrencyCode }] = useTravelWhiteLabelCurrency();
  const { value: localeCurrencyOnOrOff, loading: loadingUseLocaleCurrencyFlag } = useRemoteFeatureFlag('localeBasedHotelCurrency');
  const { value: requestPricingOnOrOff, loading: loadingShowPricingFlag } = useRemoteFeatureFlag('travelShowHotelPrice');
  const {
    value: accommodationsGuestPriceCompareType,
    loading: accommodationsGuestPriceCompareTypeLoading,
    payload: accommodationsGuestPriceCompareTypeLoadingPayload
  } = useRemoteFeatureFlag('accommodationsGuestPriceCompareType');

  const preferredProviderType = accommodationsGuestPriceCompareType as PreferredHotelProviderType;
  const priceComparisonPayload = useMemo(() => (Object(accommodationsGuestPriceCompareTypeLoadingPayload) as PriceComparisonPayload) || [], [
    accommodationsGuestPriceCompareTypeLoadingPayload
  ]);

  const payload: TravelPlacePricingAndBookingDetailsPayload | undefined = useMemo(() => {
    if (
      !targetDate ||
      !checkInDate ||
      !checkOutDate ||
      !travelPlaceIds.length ||
      loadingUseLocaleCurrencyFlag ||
      requestPricingOnOrOff !== 'on' ||
      loadingEventInfo ||
      !eventInfo?.eventId ||
      loadingCurrencyCode
    ) {
      return undefined;
    }
    if (DateFNS.isBefore(targetDate, new Date())) {
      return undefined;
    }
    // Validate priceComparisonPayload values come from amplitude
    const shouldUsePriceComparison = accommodationsGuestPriceCompareType === 'pricecomparison';
    let validPriceComparisonPayload: PriceComparisonPayload | undefined;
    if (shouldUsePriceComparison && isValidPriceComparePayload(priceComparisonPayload)) {
      validPriceComparisonPayload = priceComparisonPayload;
    }
    // Validate preferredProviderType values come from amplitude
    let validPreferredProviderType: PreferredHotelProviderType | undefined;
    if ((!shouldUsePriceComparison || !validPriceComparisonPayload) && isValidPreferredHotelProviderType(preferredProviderType)) {
      validPreferredProviderType = preferredProviderType;
    }
    return {
      currencyCode: ((localeCurrencyOnOrOff === 'on' ? currencyCode : TravelCurrencyCode.USD) || TravelCurrencyCode.USD) as TravelCurrencyCode,
      dates: {
        checkIn: {
          // validated with skip
          high: checkInDate,
          low: checkInDate
        },
        // validated with skip
        checkOut: {
          high: checkOutDate,
          low: checkOutDate
        }
      },
      occupancies: {
        occupancies: [
          {
            adults: 2,
            childrenAges: []
          }
        ],
        unitCount: 1
      },
      metadata: {
        eventId: eventInfo.eventId,
        ...(validPriceComparisonPayload && { priceComparisonPayload: validPriceComparisonPayload }),
        ...(validPreferredProviderType && { preferredProviderType: validPreferredProviderType })
      },
      ...(travelPlaceIdType === 'accommodationPlace' && { accommodationPlaceIds: travelPlaceIds })
    };
  }, [
    targetDate,
    checkInDate,
    checkOutDate,
    travelPlaceIds,
    loadingUseLocaleCurrencyFlag,
    requestPricingOnOrOff,
    loadingEventInfo,
    eventInfo?.eventId,
    loadingCurrencyCode,
    accommodationsGuestPriceCompareType,
    priceComparisonPayload,
    preferredProviderType,
    localeCurrencyOnOrOff,
    currencyCode,
    travelPlaceIdType
  ]);

  const { loading, called, error } = useTravelPlacesPricingAndBookingDetailsQuery({
    batchMode: 'fast',
    variables: {
      payload: payload!
    },
    skip: !payload
  });

  useEffect(() => {
    queryStatusVar({ loading: loading || loadingUseLocaleCurrencyFlag || loadingShowPricingFlag || accommodationsGuestPriceCompareTypeLoading, called, error });
  }, [loading, loadingUseLocaleCurrencyFlag, loadingShowPricingFlag, accommodationsGuestPriceCompareTypeLoading, called, error]);

  return {
    loading: loading || loadingUseLocaleCurrencyFlag || loadingShowPricingFlag || accommodationsGuestPriceCompareTypeLoading,
    called,
    error: error,
    checkInDate,
    checkOutDate
  };
};

const readFragmentFromCache = (client: ApolloClient<object>, travelPlaceId: string, travelPlaceIdType: 'accommodationPlace' | 'joyPlace') => {
  // Define the fragment
  const fragment = TravelPlacePricingAndBookingDetailsFragmentDoc;

  // Generate the cache ID based on the type and key fields
  let cacheId = `AccommodationPlacePricingAndBookingDetails:{"accommodationPlace":{"id":"${travelPlaceId}"}}`;
  if (travelPlaceIdType === 'joyPlace') {
    cacheId = `AccommodationPlacePricingAndBookingDetails:{"joyPlace":{"id":"${travelPlaceId}"}}`;
  }

  // Read the fragment from the cache
  const data = client.cache.readFragment<TravelPlacePricingAndBookingDetailsFragment>({
    id: cacheId,
    fragment
  });

  return data;
};

/**
 *
 * @param travelPlaceId - accommodationPlaceId to fetch pricing for
 * @param travelPlaceIdType = accommodationPlace | joyPlace - To identify which type of travelPlaceIds either accommodationPlace or joyPlace
 * @returns
 */
export const useTravelPlacesPricingAndBookingDetailsFragment = (
  travelPlaceId: Maybe<string>,
  travelPlaceIdType: 'accommodationPlace' | 'joyPlace'
): { price: Maybe<string>; strikeOutPrice: Maybe<string>; data: Maybe<TravelPlacePricingAndBookingDetailsFragment>; loading: boolean; error?: Error } => {
  const complaintRef = useRef<boolean>(false);
  const client = useApolloClient();
  const locale = useLocaleContext();
  const { loading, error } = useReactiveVar(queryStatusVar);
  if (loading) {
    return { price: undefined, strikeOutPrice: undefined, data: undefined, loading: true };
  }
  if (error) {
    return { price: null, strikeOutPrice: undefined, data: undefined, loading: false, error: error };
  }
  const data = readFragmentFromCache(client, travelPlaceId!, travelPlaceIdType);

  const priceData = data?.pricingInfo?.nightlyBaseRate;
  const minorUnits = priceData?.valueInMinorUnits;
  const minorUnitsDigits = priceData?.digits;
  const currency = priceData?.currency;
  const price = (travelPlaceId && (minorUnits && currency ? translatedPrice(minorUnits, minorUnitsDigits!, currency, locale.language) : null)) || undefined;

  const strikeOutPriceData = data?.pricingInfo?.strikeoutNightlyBaseRate;
  const strikeOutMinorUnits = strikeOutPriceData?.valueInMinorUnits;
  const strikeOutMinorUnitsDigits = strikeOutPriceData?.digits;
  const strikeOutCurrency = strikeOutPriceData?.currency;
  const strikeOutPriceValid = strikeOutMinorUnits && minorUnits && strikeOutMinorUnits > minorUnits;

  if (strikeOutMinorUnits && !strikeOutPriceValid && !complaintRef.current) {
    complaintRef.current = true;
    addError(new Error('Strikeout price is not valid'), {
      context: 'useTravelPlacesPricingAndBookingDetailsFragment',
      strikeOutPriceUnits: strikeOutMinorUnits,
      priceUnits: minorUnits,
      travelPlaceId: travelPlaceId
    });
  }
  const strikeOutPrice =
    strikeOutPriceValid && strikeOutMinorUnits && strikeOutCurrency
      ? translatedPrice(strikeOutMinorUnits, strikeOutMinorUnitsDigits!, strikeOutCurrency, locale.language)
      : undefined;
  return { price, strikeOutPrice, data, loading: loading };
};
